import { getLocalTimeZone, now } from "@internationalized/date"
import { queryOptions } from "@tanstack/react-query"
import { fetchAuthSession } from "aws-amplify/auth"

import api from "@/utilities/api"

import {
  GatedCheckoutError,
  GatedConfirmSetupIntentError,
  GatedPayError,
  GatedPaymentIntentError,
  GatedSetupIntentError,
} from "@/utilities/expectedErrors"

import {
  CHECKOUT_BASE_KEY,
  CONFIRM_PAYMENT_BASE_KEY,
  CONFIRM_SETUP_INTENT_BASE_KEY,
  PAYMENT_INTENT_BASE_KEY,
  SETUP_INTENT_BASE_KEY,
  SHARED_BASE_KEY,
} from "./keys"

export const setupIntentQueryOptions = () => {
  return queryOptions({
    queryKey: [SHARED_BASE_KEY, SETUP_INTENT_BASE_KEY],
    queryFn: async () => {
      const authSession = await fetchAuthSession()
      const token = authSession.tokens?.idToken?.toString()
      const rsp = await api.setupIntent.POST({ cognitoIDToken: token })
      if ("failReason" in rsp) {
        throw new GatedSetupIntentError(rsp.failReason)
      }
      return rsp
    },
    retry: false,
  })
}

export const confirmSetupIntentQueryOptions = (args: { sessionID: string }) => {
  return queryOptions({
    queryKey: [SHARED_BASE_KEY, CONFIRM_SETUP_INTENT_BASE_KEY, args.sessionID],
    queryFn: async () => {
      const rsp = await api.confirmSetupIntent.POST({})

      if ("failReason" in rsp) {
        throw new GatedConfirmSetupIntentError(rsp.failReason)
      }

      return rsp
    },
    retry: false,
  })
}

export const checkoutQueryOptions = (args: { sessionID: string }) => {
  return queryOptions({
    queryKey: [SHARED_BASE_KEY, CHECKOUT_BASE_KEY, args.sessionID],
    queryFn: async () => {
      const rsp = await api.checkout.POST({ sessionID: args.sessionID })

      if ("failReason" in rsp) {
        throw new GatedCheckoutError(rsp.failReason)
      }
      return rsp
    },
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

export const paymentIntentQueryOptions = (args: {
  sessionID: string
  pamID: string
}) => {
  return queryOptions({
    queryKey: [
      SHARED_BASE_KEY,
      PAYMENT_INTENT_BASE_KEY,
      args.sessionID,
      args.pamID,
    ],
    queryFn: async () => {
      const authSession = await fetchAuthSession()
      const token = authSession.tokens?.idToken?.toString()
      const rsp = await api.paymentIntent.POST({
        pamID: args.pamID,
        cognitoIDToken: token,
      })
      if ("failReason" in rsp) {
        throw new GatedPaymentIntentError(rsp.failReason)
      }
      return rsp
    },
    retry: false,
  })
}

export const confirmPaymentIntentQueryOptions = (args: {
  stripePaymentIntentID: string | null
  pamID: string
}) => {
  return queryOptions({
    queryKey: [SHARED_BASE_KEY, CONFIRM_PAYMENT_BASE_KEY],
    queryFn: async () => {
      const rsp = await api.pay.POST({
        stripePaymentIntentID: args.stripePaymentIntentID,
        termsAndConditionsOK: now(getLocalTimeZone()).toAbsoluteString(),
        pamID: args.pamID,
      })

      if ("failReason" in rsp) {
        throw new GatedPayError(rsp.failReason)
      }
      return rsp
    },
    retry: false,
  })
}
